import { Award, ShieldCheck, Users, Compass } from "lucide-react";

const CATEGORY_META = {
  trust:      { label: "Trust",      icon: ShieldCheck, tone: "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/20 dark:border-emerald-900/40 dark:text-emerald-200" },
  teaching:   { label: "Teaching",   icon: Award,       tone: "bg-indigo-50 border-indigo-200 text-indigo-800 dark:bg-indigo-900/20 dark:border-indigo-900/40 dark:text-indigo-200" },
  experience: { label: "Experience", icon: Users,       tone: "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-900/40 dark:text-amber-200" },
  growth:     { label: "Growth",     icon: Compass,     tone: "bg-purple-50 border-purple-200 text-purple-800 dark:bg-purple-900/20 dark:border-purple-900/40 dark:text-purple-200" },
};

/**
 * AchievementBadgesPanel — auto-computed verified badges.
 *
 * Shows earned badges grouped by category. Unearned badges can optionally
 * be shown faded as "available" guidance to the instructor.
 */
export default function AchievementBadgesPanel({ achievements, showUnearned = false }) {
  if (!achievements) return null;
  const earned = achievements.earned || [];
  const catalogue = achievements.catalogue || earned; // catalogue only present on /me endpoint

  const earnedByCat = {};
  for (const b of earned) {
    (earnedByCat[b.category] ||= []).push(b);
  }

  const catalogueByCat = {};
  for (const b of (showUnearned ? catalogue : earned)) {
    (catalogueByCat[b.category] ||= []).push(b);
  }

  const allCats = showUnearned
    ? Object.keys(CATEGORY_META)
    : Object.keys(earnedByCat);

  if (allCats.length === 0) {
    return (
      <div data-testid="achievements-empty" className="rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-dashed border-slate-200 dark:border-slate-700 p-4 text-center">
        <Award className="w-5 h-5 mx-auto text-slate-400 mb-1" />
        <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">No badges earned yet</p>
        <p className="text-[11px] text-slate-400 mt-1">Badges unlock automatically as verified platform activity builds up.</p>
      </div>
    );
  }

  const earnedKeys = new Set(earned.map((b) => b.key));

  return (
    <div data-testid="achievements-panel" className="space-y-3">
      {allCats.map((cat) => {
        const meta = CATEGORY_META[cat] || { label: cat, icon: Award, tone: "" };
        const badges = catalogueByCat[cat] || [];
        if (badges.length === 0) return null;
        const Icon = meta.icon;
        return (
          <section key={cat} className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-2 mb-2 text-slate-500 dark:text-slate-400">
              <Icon className="w-4 h-4" />
              <p className="text-xs font-extrabold uppercase tracking-wider">{meta.label}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {badges.map((b) => {
                const isEarned = earnedKeys.has(b.key);
                return (
                  <div
                    key={b.key}
                    data-testid={`achievement-${b.key}`}
                    title={b.description + (b.evidence ? ` — ${b.evidence}` : "")}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-extrabold rounded-full border transition ${
                      isEarned ? meta.tone : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 opacity-60"
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                    {b.label}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
